import type { OrgBaseInterface } from "../Shared/OrgBase.Interace";

/**
 * Hours-of-operation domain types. Backs the structured replacement for the
 * freeform `OfficeHours` / `AccessHours` / `CallCenterHours` / `SpecialHours`
 * vars. See `libs/server-helpers/src/Schedule/Schedule.Service.ts`.
 *
 * Inheritance shape mirrors `LocationVariableEntity`: rows live at one of
 * three scopes — org (groupId null, locationId null), group (groupId set,
 * locationId null), or location (locationId set). Resolution uses
 * `findCascaded` from `@swivl-mono/helpers` for parity with every other
 * cascading-data surface in the platform.
 *
 * Agent working-hours intentionally NOT modeled here — that's a different
 * domain (people, not places) and gets its own entity later.
 */

/** Canonical schedule slugs the bot understands. Extend `SCHEDULE_SLUG_META` below when adding more. */
export type ScheduleSlug = "office-hours" | "gate-access" | "call-center" | "agent-default" | "sequence";

/**
 * Per-slug metadata. Drives sane defaults in the holiday-rule UI ("Christmas
 * Day" should default to closing the office and call-center but NOT gate
 * access — storage facilities are 365/24/7 for tenant access).
 */
export interface ScheduleSlugMeta {
  slug: ScheduleSlug;
  label: string;
  /**
   * Whether new HolidayRules should default to including this slug in
   * `appliesToSlugs`. Office / call-center: true. Gate access: false
   * (closing the gate on holidays violates the storage-industry norm).
   */
  observesHolidaysByDefault: boolean;
  /**
   * The hours a location runs on before anyone saves a row for this group at any scope
   * (ENG-3216 decision 49). ONE source: the Hours page renders it with the "Default" pill and
   * seeds Edit from it, and every consumer (the sequences engine, open-status) falls back to it.
   */
  defaultWeekly: readonly WeeklyPeriod[];
}

const WEEKDAYS: readonly WeeklyPeriod["dayOfWeek"][] = [1, 2, 3, 4, 5];
const EVERY_DAY: readonly WeeklyPeriod["dayOfWeek"][] = [0, 1, 2, 3, 4, 5, 6];

/** 9:00 AM–5:00 PM, Monday–Friday — the standard default for every schedule group but Gate Access (decision 49). */
export const STANDARD_DEFAULT_WEEKLY: readonly WeeklyPeriod[] = WEEKDAYS.map((dayOfWeek) => ({
  dayOfWeek,
  openMinute: 9 * 60,
  closeMinute: 17 * 60
}));

/** Open around the clock, every day — the Gate Access default (decision 49). */
export const ALWAYS_OPEN_WEEKLY: readonly WeeklyPeriod[] = EVERY_DAY.map((dayOfWeek) => ({ dayOfWeek, openMinute: 0, closeMinute: 24 * 60 }));

/** 9:00 AM–5:00 PM every day — the Sequence Sending Hours default (decision 49, amended 2026-09-21: reminders go out on weekends too). */
export const EVERY_DAY_9_TO_5_WEEKLY: readonly WeeklyPeriod[] = EVERY_DAY.map((dayOfWeek) => ({
  dayOfWeek,
  openMinute: 9 * 60,
  closeMinute: 17 * 60
}));

export const SCHEDULE_SLUG_META: readonly ScheduleSlugMeta[] = [
  { slug: "office-hours", label: "Office Hours", observesHolidaysByDefault: true, defaultWeekly: STANDARD_DEFAULT_WEEKLY },
  { slug: "gate-access", label: "Gate Access", observesHolidaysByDefault: false, defaultWeekly: ALWAYS_OPEN_WEEKLY },
  { slug: "call-center", label: "Call Center Hours", observesHolidaysByDefault: true, defaultWeekly: STANDARD_DEFAULT_WEEKLY },
  { slug: "agent-default", label: "Agent Default", observesHolidaysByDefault: true, defaultWeekly: STANDARD_DEFAULT_WEEKLY },
  // Sequence sending hours (ENG-2987): when the new sequences engine may message
  // tenants. A closed day defers a reminder; holidays pause dunning by default.
  // Default 9–5 every day (decision 49, amended); the 08:00–21:00 legal quiet-hours floor
  // is a separate, hard limit the engine applies on top.
  { slug: "sequence", label: "Sequence Sending Hours", observesHolidaysByDefault: true, defaultWeekly: EVERY_DAY_9_TO_5_WEEKLY }
];

/**
 * A schedule group as the Hours page and the Sequences page list them: the built-ins from
 * `SCHEDULE_SLUG_META` plus the org's custom groups (ENG-3216 decision 50). A custom group is
 * a slug (`custom-<name>`) whose label lives on its org-scope `schedule` row.
 */
export interface ScheduleGroup {
  slug: string;
  label: string;
  builtIn: boolean;
}

export const CUSTOM_SCHEDULE_SLUG_PREFIX = "custom-";

export function isCustomScheduleSlug(slug: string): boolean {
  return slug.startsWith(CUSTOM_SCHEDULE_SLUG_PREFIX);
}

/** True for the built-in schedule groups; anything else is an operator-created group (its slug is free text). */
export function isBuiltInScheduleSlug(slug: string): slug is ScheduleSlug {
  return SCHEDULE_SLUG_META.some((meta) => meta.slug === slug);
}

/** The hours a group runs on with no saved row anywhere: the built-in's own default, or the standard weekdays 9–5 for a custom group. */
export function defaultWeeklyForSlug(slug: string): readonly WeeklyPeriod[] {
  return SCHEDULE_SLUG_META.find((meta) => meta.slug === slug)?.defaultWeekly ?? STANDARD_DEFAULT_WEEKLY;
}

/** The default `appliesToSlugs` set for a brand-new HolidayRule. */
export const DEFAULT_HOLIDAY_APPLIES_TO_SLUGS: readonly ScheduleSlug[] = SCHEDULE_SLUG_META.filter((m) => m.observesHolidaysByDefault).map(
  (m) => m.slug
);

/**
 * One open period within a single day. `closeMinute` may be > 1440 to express
 * a midnight-crossing period. Split shifts (9-12, 1-5) are TWO entries with
 * the same `dayOfWeek`.
 */
export interface WeeklyPeriod {
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  openMinute: number;
  closeMinute: number;
  label?: string;
}

/**
 * Recurring weekly schedule for a (orgId, groupId?, locationId?, slug) tuple.
 *
 * Inheritance: location > group > org via `findCascaded`. A location row,
 * if present, fully replaces an inherited group/org row (wholesale, not
 * per-day cascade).
 */
export interface ScheduleInterface extends OrgBaseInterface {
  /** Null for org-scope and location-scope rows; set for group-scope rows. */
  groupId: number | null;
  /** Null for org-scope and group-scope rows; set for location-scope rows. */
  locationId: number | null;
  /** Canonical key, e.g. `"office-hours"`. */
  slug: string;
  label: string;
  weekly: WeeklyPeriod[];
  isActive: boolean;
}

/**
 * Single-date override for a schedule. Highest priority in the resolver
 * (exception > holiday > weekly). Lives at one of three scopes via
 * `(groupId, locationId)` — same shape as Schedule and HolidayRule.
 *
 * Resolution: walk scopes most-specific first (location → group → org),
 * first match by `(slug, date)` wins. An org-level exception applies to
 * every location's matching slug unless a narrower scope overrides it
 * for the same (slug, date).
 *
 * Per-slug — `slug` is required so an "office-hours" exception doesn't
 * accidentally close the gate. Author one row per (slug, date, scope).
 */
export interface ScheduleExceptionInterface extends OrgBaseInterface {
  groupId: number | null;
  locationId: number | null;
  slug: string;
  /** ISO `YYYY-MM-DD`, interpreted in the location's effective timezone. */
  date: string;
  isClosed: boolean;
  periods: WeeklyPeriod[] | null;
  reason: string;
}

/**
 * Recurring closure rule. Lives at one of three scopes (same as Schedule)
 * via `(groupId, locationId)` columns. Resolution: applicable rules at a
 * given location = union of (location's own rules) ∪ (its group's rules) ∪
 * (the org's rules) — every level publishes, locations don't replace
 * inherited rules.
 *
 * The slug-targeting via `appliesToSlugs` lets one rule selectively close
 * some schedule slugs but not others. Defaults are pre-populated from
 * `SCHEDULE_SLUG_META.observesHolidaysByDefault` when authoring.
 */
export interface HolidayRuleInterface extends OrgBaseInterface {
  groupId: number | null;
  locationId: number | null;
  label: string;
  /** iCalendar RFC 5545 RRULE (no DTSTART). */
  rrule: string;
  isClosed: boolean;
  periods: WeeklyPeriod[] | null;
  /**
   * Explicit list of schedule slugs this rule affects. Always non-null;
   * empty array means the rule is effectively disabled. New rules created
   * via the UI default to `DEFAULT_HOLIDAY_APPLIES_TO_SLUGS` (office +
   * call-center, NOT gate access).
   */
  appliesToSlugs: string[];
}

/**
 * Result of "is open at time T" for a single (location, slug). Modeled
 * after `opening_hours.js` — bare `isOpen` isn't enough; the bot needs
 * `nextChangeAt` + `reason` to compose answers like "we open at 9am
 * tomorrow" or "we're closed for Christmas Day."
 */
export interface OpenStatusResult {
  isOpen: boolean;
  nextChangeAt: string | null;
  nextChangeTo: "open" | "closed" | null;
  /**
   * Why the verdict is what it is. There is no "no schedule" outcome: a group with no saved
   * row anywhere runs on its default hours, which are first-class data (ENG-3216 decision 56).
   */
  reason: "regular" | "holiday" | "exception" | "always_closed";
  appliedRule: {
    /** Which scope's row the verdict came from — `default` when no row is saved at any scope. */
    source: "org" | "group" | "location" | "default";
    scheduleId: number | null;
    exceptionId?: number;
    holidayRuleId?: number;
  };
  timezone: string;
}

/**
 * The hours in force for one (location, group) + where they came from. Returned by
 * `ScheduleService.resolveSchedule`. With no saved row at any scope the group's default hours
 * resolve, `source: "default"`, `id: null` — never "nothing" (ENG-3216 decision 56).
 */
export interface ResolvedSchedule {
  schedule: Pick<ScheduleInterface, "slug" | "label" | "weekly"> & { id: number | null };
  /** Which scope owned the winning row, or `default` for the built-in hours. */
  source: "org" | "group" | "location" | "default";
}
